import { useQuery } from '@apollo/client'
import { Box, Button, Paper, Link } from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
  GridValueFormatterParams,
  getGridSingleSelectOperators,
  GridToolbar,
} from '@mui/x-data-grid'
import {
  Brand,
  LeadImport,
  Query,
  Retailer,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'

import ImportLeadDialog from '@src/components/leads/ImportLeadDialog'
import FilterContentLayoutWrapper from '@src/components/shared/FilterContentLayoutWrapper'
import LinkChild from '@src/lib/LinkChild'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'
import { selectCurrentRetailerAccountBrandsMenuData } from '@src/stores/mainMenu/selectors'

import leadsPaths from './leadPaths'
import { GET_LEADS } from './useLeads'

interface BrandList {
  id: string
  name: string
  value: string | number
  label: string
}

const Leads: React.FC<React.PropsWithChildren> = () => {
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const { brands } = useSelector(selectCurrentRetailerAccountBrandsMenuData)
  const [isOpen, setOpen] = useState(false)
  const { activeTab = 'list', retailerAccountSlug } = useParams<{
    activeTab?: string
    retailerAccountSlug: string
  }>()

  const getBrandName = (params: GridValueFormatterParams) => {
    const brand = brands.find(
      (brand: BrandList) => String(brand.id) === String(params.value),
    )
    return brand ? brand.name : ''
  }

  const sortBrandByName = (id1: string, id2: string) => {
    const name1 = getBrandName({ value: id1 } as GridValueFormatterParams)
    const name2 = getBrandName({ value: id2 } as GridValueFormatterParams)
    return name1.localeCompare(name2)
  }

  const { data, loading, refetch } = useQuery<Query>(GET_LEADS, {
    variables: {
      resourceId: retailerAccount.id,
      resourceType: 'retailer_accounts',
      fields: ['leads_count', 'leads_unsubscribed_count'],
      params: {
        filters: {
          by_state: 'unsubscribed',
        },
      },
    },
  })

  const brandList = useMemo(
    () =>
      brands
        .map((el: Brand) => {
          return {
            id: el.id,
            name: el.name,
            value: String(el.id),
            label: el.name,
            retailerId: retailerAccount.retailers.find(
              (retailer: Retailer) => retailer.brand_id === el.id,
            )?.id,
          }
        })
        .filter((el: BrandList) => el.id),
    [brands, retailerAccount.retailers],
  )

  const unsubscribedColumn: GridColDef[] = [
    {
      field: 'email_address',
      headerName: I18n.t('shared.emailAddress'),
      flex: 1,
      type: 'string',
    },
    {
      field: 'brand_id',
      headerName: 'Brand',
      flex: 1,
      type: 'singleSelect',
      valueOptions: brandList,
      filterOperators: getGridSingleSelectOperators().filter(
        (operator) => operator.value === 'is',
      ),
      valueFormatter: getBrandName,
      sortComparator: sortBrandByName,
    },
    {
      field: 'import_campaign',
      headerName: I18n.t('leads.importColumn'),
      flex: 0.5,
      filterable: false,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.lead_import_id || params.row.campaign_id || ''}`,
    },
  ]

  const customerListColumns: GridColDef<LeadImport>[] = [
    {
      field: 'name',
      headerName: I18n.t('shared.name'),
      flex: 1,
      type: 'string',
      renderCell: (params: GridRenderCellParams) => (
        <LinkChild
          to={leadsPaths.import.build({
            retailerAccountSlug,
            importId: params.row.id,
          })}
        >
          <Link>{params.row.name}</Link>
        </LinkChild>
      ),
    },
    {
      field: 'brand_id',
      headerName: 'Brand',
      flex: 1,
      type: 'singleSelect',
      valueOptions: brandList,
      filterOperators: getGridSingleSelectOperators().filter(
        (operator) => operator.value === 'is',
      ),
      valueFormatter: getBrandName,
      sortComparator: sortBrandByName,
    },
    {
      field: 'leads_count',
      headerName: I18n.t('leads.customerCount'),
      type: 'number',
      flex: 0.5,
      filterable: false,
    },
    {
      field: 'leads_unsubscribed_count',
      headerName: I18n.t('leads.unsubscribedCount'),
      type: 'number',
      flex: 0.5,
      filterable: false,
    },
  ]

  return (
    <FilterContentLayoutWrapper
      title={I18n.t('nav.customers')}
      slotProps={{
        tab: {
          selectedTab: activeTab,
          tabs: [
            {
              label: I18n.t('leads.customerListsTab.tabTitle'),
              value: 'list',
              to: leadsPaths.index.build({
                retailerAccountSlug,
                activeTab: 'list',
              }),
            },
            {
              label: I18n.t('leads.unsubscribedTab.tabTitle'),
              value: 'unsubscribed',
              to: leadsPaths.index.build({
                retailerAccountSlug,
                activeTab: 'unsubscribed',
              }),
            },
          ],
        },
      }}
    >
      <Box
        sx={{
          marginTop: 1,
        }}
      >
        {activeTab === 'list' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              marginTop: 1,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={isOpen}
              onClick={() => setOpen(true)}
              sx={{ marginLeft: 'auto', marginBottom: 2 }}
            >
              {`+ ${I18n.t('leads.addList')}`}
            </Button>
            <Paper sx={{ height: '70vh', width: '100%' }}>
              <DataGrid
                slots={{ toolbar: GridToolbar }}
                loading={loading}
                rows={data?.getLeadImports || []}
                columns={customerListColumns}
                pagination
                autoPageSize
              />
            </Paper>
          </Box>
        )}

        {activeTab === 'unsubscribed' && (
          <Paper sx={{ height: '70vh', width: '100%' }}>
            <DataGrid
              slots={{ toolbar: GridToolbar }}
              loading={loading}
              rows={data?.getLeadsList || []}
              columns={unsubscribedColumn}
              pagination
              autoPageSize
            />
          </Paper>
        )}
      </Box>

      <ImportLeadDialog
        brandList={brandList}
        isOpen={isOpen}
        handleClose={() => setOpen(false)}
        handleSuccess={refetch}
      />
    </FilterContentLayoutWrapper>
  )
}

export default Leads
